/**
 * Der Haus-Filter des Reiters *Energie* (Spec 46a, „Web · Filter-Form") in
 * seinen zwei Formen mit denselben Bezeichnern: als Einträge der
 * Kontext-Sidebar ab 768 px (`form="sidebar"`, von `router.tsx::EnergieSidebar`
 * in `ContextSidebar` gesetzt) und als Chipzeile über der Liste darunter
 * (`form="chips"`, von `routes/Energie.tsx`). Gemountet wird immer nur eine
 * Form (`useIsDesktopViewport`), sonst stünden die Bezeichner doppelt im
 * Dokument.
 *
 * Der Filter steht in der URL (`?haus=`); ein Wechsel lässt das geöffnete
 * Detail los (`zaehler` entfällt), weil der Zähler im neuen Ausschnitt fehlen
 * kann. Aktiv ist der Akzent (`chipClass`-Bauform der Aufgabenfilter, Spec 39).
 */

import { Link } from '@tanstack/react-router'

import { SidebarItem } from '@/components/SidebarItem'
import type { MeterHouse } from '@/features/energy/api'
import { houseFilterEntries } from '@/features/energy/house-filter'

interface HouseFilterProps {
  readonly houses: readonly MeterHouse[]
  /** `haus` aus der URL; unbekannte Werte zeigen „Alle". */
  readonly selected: string | undefined
  readonly form: 'sidebar' | 'chips'
}

const CHIP_CLASS =
  'rounded-card text-body flex min-h-(--tap-min) shrink-0 items-center border px-3 whitespace-nowrap ' +
  'transition-colors duration-(--duration-fast) ease-(--ease-out)'

export function HouseFilter({ houses, selected, form }: HouseFilterProps) {
  if (form === 'sidebar') {
    return houseFilterEntries(houses, selected, 'Alle Zähler').map((entry) => (
      <SidebarItem
        key={entry.testId}
        to="/energie"
        search={{ haus: entry.houseId, zaehler: undefined }}
        label={entry.label}
        testId={entry.testId}
        count={entry.count}
        isActive={entry.isActive}
      />
    ))
  }

  return (
    <nav
      aria-label="Haus"
      data-testid="energy-house-filter"
      className="border-border flex gap-2 overflow-x-auto border-b px-(--page-x) py-2"
    >
      {houseFilterEntries(houses, selected, 'Alle').map((entry) => (
        <Link
          key={entry.testId}
          to="/energie"
          search={{ haus: entry.houseId, zaehler: undefined }}
          data-testid={entry.testId}
          // `exact`: sonst trifft die leere Suche von „Alle" jede URL (Befund P1).
          activeOptions={{ exact: true }}
          aria-current={entry.isActive ? 'page' : undefined}
          className={`${CHIP_CLASS} ${
            entry.isActive ? 'border-accent bg-accent/10 text-accent' : 'border-border bg-surface text-text hover:bg-bg'
          }`}
        >
          {entry.label}
        </Link>
      ))}
    </nav>
  )
}
